/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import { PURPLE } from '@atlassian/jira-issue-epic-color/src/common/types.tsx';
import { RoadmapChartDragLayer } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/chart-overlay/drag-layer/index.tsx';
import type { DragLayerMeta } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/chart-overlay/drag-layer/types.tsx';
import { RoadmapChartOverlay } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/chart-overlay/index.tsx';
import { zIndex } from '@atlassian/timeline-table/common/constants';
import { useHeaderState } from '@atlassian/timeline-table/common/context/header';
import {
	DEPENDENCY_DRAG_AND_DROP,
	type InteractionMetricType,
} from '@atlassian/jira-software-roadmap-utils/src/utils/metrics/common/constants.tsx';
import { DEPENDENCY_DRAG_ITEM_ID } from '../../../common/constants.tsx';
import { DndLongTaskMetric } from '../../../common/ui/metrics/dnd-metrics/index.tsx';
import { getColor } from '../../../common/utils/get-color.tsx';
import type { TimelineViewIssue } from '../../../services/get-timeline-view-issues/types.tsx';
import { DependencyLinePreview } from './dependency-line-preview/index.tsx';
import { ChartOverlayButton } from './overlay-button/index.tsx';
import type { DependencyDragItem } from './types.tsx';
import { getPreviewProps } from './utils.tsx';

type Props = {
	issuesMap: Map<string, TimelineViewIssue>;
};

const DndLongTaskMetricComponent = (props: { isDragging: boolean }) => (
	<DndLongTaskMetric<InteractionMetricType> {...props} metricType={DEPENDENCY_DRAG_AND_DROP} />
);

const renderOverlayButton = () => <ChartOverlayButton />;

export const ChartOverlay = ({ issuesMap }: Props) => {
	const [{ headerHeight }] = useHeaderState();
	const renderPreview = (props: DragLayerMeta<DependencyDragItem>) => {
		const previewProps = getPreviewProps(props);

		if (!previewProps) {
			return null;
		}

		const item = issuesMap.get(previewProps.id);
		const color = item
			? getColor(item.status.categoryId)
			: // We are mapping Color to design tokens in transformColorToTheme
				PURPLE;

		return <DependencyLinePreview {...previewProps} color={color} />;
	};

	const renderChartDragLayer = () => (
		<RoadmapChartDragLayer
			type={DEPENDENCY_DRAG_ITEM_ID}
			renderPreview={renderPreview}
			DndLongTaskMetric={DndLongTaskMetricComponent}
		/>
	);

	return (
		<DragLayerContainer top={headerHeight}>
			<RoadmapChartOverlay
				renderChartDragLayer={renderChartDragLayer}
				renderOverlayButton={renderOverlayButton}
			/>
		</DragLayerContainer>
	);
};

const dragLayerContainerBaseStyles = css({
	position: 'absolute',
	right: 0,
	bottom: 0,
	left: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: zIndex.HIGHEST,
});

const DragLayerContainer = ({ top, children }: { top: number; children: React.ReactNode }) => (
	<div
		css={dragLayerContainerBaseStyles}
		// eslint-disable-next-line jira/react/no-style-attribute
		style={{
			top: `${top}px`,
		}}
	>
		{children}
	</div>
);
